import { IGenericBaryFormGroup } from '../GenericBaryFormGroup';
import { IFormBuilder } from '../IFormBuilder';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { ContactControls } from './ContactFormBuilder';
import { TransportFormGroup } from '../TransportFormGroup';
import { BaryValidator } from '../../../validators/bary.validator';
import { TransportItemControls } from './TransportItemFormBuilder';
import { BaryFormFactoryService } from '../../../services/bary-form-factory.service';
import { AddressFormGroup } from '../AddressFormGroup';
import { Transporter } from '../../transporter.model';
import { Transport } from '../../transport.model';
import { TransportExtraConditionControls } from '../../transport-extra-condition.controls';
import { TransporterEnum } from '../../enums/transporter-enum.enum';
import { TransportItem } from '../../transport-item.model';
import { OfferServiceTypeEnum } from '../../enums/offer-service-type.enum';
import { IPricingTransporterConfiguration } from '../../pricing-transporter.configuration';
import { OfferMerchandiseTypeEnum } from '../../enums/offer-merchandise-type.enum';

interface ITransportFormBuilder extends IFormBuilder<TransportControls> {
  init(config: IPricingTransporterConfiguration, transporter: Transporter, transport?: Transport): ITransportFormBuilder;

  build(): IGenericBaryFormGroup<TransportControls>;
}

export type TransportControls = {
  id: FormControl;
  itemsNature: FormControl;
  goodsValue: FormControl;
  euroPallet: FormControl;
  pickupAddress: AddressFormGroup;
  pickupContact: IGenericBaryFormGroup<ContactControls>;
  pickupReference: FormControl;
  pickupDate: FormControl;
  pickupLoadingConstraint: FormControl;
  pickupAccessConstraint: FormControl;
  pickupPhoneCall: FormControl;
  deliveryAddress: AddressFormGroup;
  deliveryContact: IGenericBaryFormGroup<ContactControls>;
  deliveryReference: FormControl;
  deliveryDate: FormControl;
  deliveryMeeting: FormControl;
  deliveryLoadingConstraint: FormControl;
  deliveryAccessConstraint: FormControl;
  deliveryPhoneCall: FormControl;
  insurance: FormControl;
  items: IGenericBaryFormGroup<TransportItemControls>[];
  insuranceItem: FormControl;
  pickupRegisteredAddressId: FormControl;
  deliveryRegisteredAddressId: FormControl;
  pickupMeeting: FormControl;
  pickupComments: FormControl;
  deliveryComments: FormControl;
  pickupFloor: FormControl;
  deliveryFloor: FormControl;
  outsideEuDeliveryCountry: FormControl;
  outsideEuDeliveryCity: FormControl;
  outsideEuDeliveryComments: FormControl;
  itemsCondition: FormControl;
  pickupTimeslotStart: FormControl;
  pickupTimeslotEnd: FormControl;
  deliveryTimeslotStart: FormControl;
  deliveryTimeslotEnd: FormControl;
  offerId?: FormControl;
  transportExtraConditions: IGenericBaryFormGroup<TransportExtraConditionControls>[];

  pickupAddressGpsCoords: FormControl;
  deliveryAddressGpsCoords: FormControl;
  transporter: FormControl;
  quotationRejectionMessage: FormControl;

  merchandiseType: FormControl; // CONDITIONNE OU NON CONDITIONNE
  serviceType: FormControl; // FTL OU LTL
  isControlledTemperature: FormControl;
  isHazardousMaterial: FormControl;
};

export class TransportFormBuilder implements ITransportFormBuilder {
  private formName = 'transportForm';
  private controls!: TransportControls;
  private validators?: ValidatorFn[];

  constructor(private bcf: BaryFormFactoryService) {}

  public init(config: IPricingTransporterConfiguration, transporter: Transporter, transport?: Transport): TransportFormBuilder {
    this.controls = this.bcf.makeTransportControls(config, transporter.id!, transport);
    this.addTransportItems(config, transport);
    this.initListeners();
    if (TransporterEnum.EVOLUTRANS.is(transporter.technicalName)) {
      this.applyPickupMandatoryContact();
    } else {
      this.applyTransportValidators();
    }
    return this;
  }

  private initListeners(): void {
    // Si le service en FTL, il n'y a pas de contrainte d'accès. Par défaut, elles sont obligatoires.
    this.controls.serviceType.valueChanges.subscribe((val: OfferServiceTypeEnum): void => {
      if (val === OfferServiceTypeEnum.FTL) {
        this.controls.pickupAccessConstraint.removeValidators(Validators.required);
        this.controls.deliveryAccessConstraint.removeValidators(Validators.required);
      } else {
        this.controls.pickupAccessConstraint.addValidators(Validators.required);
        this.controls.deliveryAccessConstraint.addValidators(Validators.required);
      }
    });
  }

  private addTransportItems(config: IPricingTransporterConfiguration, transport?: Transport): void {
    if (transport) {
      // ajoute autant de transportItemControl que d'item présent.
      transport.items?.forEach((it: TransportItem): void => {
        const transportItem = this.bcf.makeTransportItemForm(config, it, transport.merchandiseType.code as OfferMerchandiseTypeEnum);
        this.controls.items.push(transportItem);
      });
    }
  }

  private applyTransportValidators(): void {
    this.validators = [
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.pickupMeeting,
        [
          [this.controls.pickupContact.controls.mail, this.controls.pickupContact.controls.phone],
          [this.controls.pickupContact.controls.firstName, this.controls.pickupContact.controls.lastName],
        ],
        'meetingRequirement',
        'estimation-tunnel.meetingRequirement',
        this.controls.pickupContact.getAngularForm()
      ),
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.deliveryMeeting,
        [[this.controls.deliveryContact.controls.phone], [this.controls.deliveryContact.controls.firstName, this.controls.deliveryContact.controls.lastName]],
        'meetingRequirement',
        'estimation-tunnel.meetingRequirementDelivery',
        this.controls.deliveryContact.getAngularForm()
      ),
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.pickupPhoneCall,
        [[this.controls.pickupContact.controls.phone], [this.controls.pickupContact.controls.firstName, this.controls.pickupContact.controls.lastName]],
        'phoneCallRequirement',
        'estimation-tunnel.phoneCallRequirement',
        this.controls.pickupContact.getAngularForm()
      ),
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.deliveryPhoneCall,
        [[this.controls.deliveryContact.controls.phone], [this.controls.deliveryContact.controls.firstName, this.controls.deliveryContact.controls.lastName]],
        'phoneCallRequirement',
        'estimation-tunnel.phoneCallRequirement',
        this.controls.deliveryContact.getAngularForm()
      ),
    ];
  }

  private applyPickupMandatoryContact(): void {
    this.controls.pickupContact.controls.phone.addValidators(Validators.required);
    this.validators = [
      BaryValidator.checkAtLeastOneForGroup(
        [[this.controls.pickupContact.controls.firstName, this.controls.pickupContact.controls.lastName]],
        'atLeastOneFirstNameOrLastName',
        'estimation-tunnel.atLeastOneFirstNameOrLastName',
        this.controls.pickupContact.getAngularForm()
      ),
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.deliveryMeeting,
        [[this.controls.deliveryContact.controls.phone], [this.controls.deliveryContact.controls.firstName, this.controls.deliveryContact.controls.lastName]],
        'meetingRequirement',
        'estimation-tunnel.meetingRequirementDelivery',
        this.controls.deliveryContact.getAngularForm()
      ),
      BaryValidator.checkAtLeastOneForGroupWithActiveControl(
        this.controls.deliveryPhoneCall,
        [[this.controls.deliveryContact.controls.phone], [this.controls.deliveryContact.controls.firstName, this.controls.deliveryContact.controls.lastName]],
        'phoneCallRequirement',
        'estimation-tunnel.phoneCallRequirement',
        this.controls.deliveryContact.getAngularForm()
      ),
    ];
  }

  build(): TransportFormGroup {
    return new TransportFormGroup(this.formName, this.controls, this.validators);
  }
}
